import {
  CreateCaseDto,
  DataBlockVariant,
  QueryFactory,
} from '@lmt/shared-types';

import { Loading } from '@lmt/unicorn-shared/app/components/common/Loading';
import {
  Button,
  ButtonColor,
  ButtonWidth,
} from '@lmt/unicorn-shared/app/components/common/ui-kit/buttons/Button';

import { getLatestCase } from '@lmt/unicorn-shared/app/components/patients/common';

import tableStyles from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable.module.scss';
import { pages } from '@lmt/unicorn-shared/app/constants/sharedRoutes';
import { useTabs } from '@lmt/unicorn-shared/app/features/navigation/operations';
import { convertToErrorString } from '@lmt/unicorn-shared/app/helpers/form/useErrorHandler';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';

import AddCaseIcon from '@lmt/unicorn-shared/app/icons/new-case.svg?react';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { startOfToday } from 'date-fns';
import Logger from 'js-logger';
import React, { useEffect, useRef, useState } from 'react';
import { Freeze } from 'react-freeze';
import { useQueryClient } from '@tanstack/react-query';
import { QueryMetaProvider } from 'react-query-swagger';
import { CaseBar } from './components/navigation/CaseBar';
import styles from './PatientCases.module.scss';
import {
  copyPreviousValues,
  isAnyFieldBasedOnPrevious,
} from '../../../measurements/copy-datablock-field/copy-previous-values-async-helper';
import { useGlobalForms } from '../../../global-forms/useGlobalForms';
import { CaseWrapper } from './CaseWrapper';
import { useEventCallback } from '@mui/material';
import { isEmpty } from '../../../measurements/copy-datablock-field/common';
import { pauseQueryInvalidationRequests } from '../../../service/startup/signalr/SignalRHandler';

interface PatientOverviewProps {
  patientId: number;
  caseId?: number | null;
}

export const PatientCases: React.FC<PatientOverviewProps> = React.memo(
  function PatientCases(props) {
    const { patientId, caseId } = props;
    const tabs = useTabs();

    const i18next = useScopedTranslation('patients.cases');
    const forms = useGlobalForms();
    const [isCaseBeingDeleted, setCaseBeingDeleted] = useState(false);
    const [isCaseBeingAdded, setCaseBeingAdded] = useState(false);

    const queryClient = useQueryClient();
    const dataBlockTypesQuery = QueryFactory.DataBlockTypeQuery.useGetQuery();
    const copyPreviousValuesForNewDefaultDataBlocks = useEventCallback(
      async (caseId: number) => {
        const autocreatedCaseBlocks =
          dataBlockTypesQuery.data?.filter(
            (x) =>
              (x.isAutoCreated || x.value === 'Information') &&
              x.variant === DataBlockVariant.Case,
          ) ?? [];
        const blocksWithPreviousFields = autocreatedCaseBlocks.filter((x) =>
          isAnyFieldBasedOnPrevious(queryClient, x, dataBlockTypesQuery.data!),
        );
        if (isEmpty(blocksWithPreviousFields)) return;

        const dataBlocks = await QueryFactory.DataBlockClient.getWithValues(
          props.patientId,
          caseId,
        );
        for (const dataBlock of dataBlocks.data) {
          await copyPreviousValues(
            dataBlock,
            forms.openCopyDataBlockValue,
            modals,
            queryClient,
          );
        }
      },
    );

    const addCase = useEventCallback(async () => {
      const cancelPauseInvalidationRequests = pauseQueryInvalidationRequests();
      try {
        setCaseBeingAdded(true);
        const startDate = startOfToday();
        const newCase: CreateCaseDto = {
          patientId: patientId,
          startDate: startDate,
          endDate: null,
        };
        const createdCase = await QueryFactory.CaseClient.createCase(newCase);
        const newData: typeof casesQuery.data = {
          totalCount: casesQuery.data!.totalCount + 1,
          data: [createdCase, ...casesQuery.data!.data],
        };
        queryClient.setQueryData(
          QueryFactory.CaseQuery.getCasesQueryKey(patientId),
          newData,
        );
        await copyPreviousValuesForNewDefaultDataBlocks(createdCase.id);
        openCaseTabOrEmpty(createdCase.id);
      } catch (e) {
        await modals.showError({ text: convertToErrorString(e) });
      } finally {
        setCaseBeingAdded(false);
        setTimeout(() => cancelPauseInvalidationRequests(), 200);
      }
    });

    const modals = useModal();

    const openCaseTabOrEmpty = useEventCallback(
      (caseId?: number, replace: boolean = false) => {
        tabs.changeTabPath(
          caseId
            ? pages.patientCases.link({
                patientId: props.patientId,
                caseId: caseId ?? ('' as any),
              })
            : pages.patientCaseLatest.link({ patientId: props.patientId }),
          replace,
        );
      },
    );

    const casesQuery = QueryFactory.CaseQuery.useGetCasesQuery({
      patientId: props.patientId,
    });

    const lastSelectedCaseIdRef = useRef(props.caseId);
    useEffect(
      function updateLastSelectedCaseIdWhenCaseChanges() {
        if (caseId) {
          lastSelectedCaseIdRef.current = caseId;
        }
      },
      [caseId],
    );
    useEffect(
      function openLastSelectedCaseIfNothingIsOpened() {
        if (!casesQuery.data?.data) return;

        const isSelectedCaseRemoved =
          props.caseId &&
          !casesQuery.data.data.find((x) => x.id === props.caseId);

        if (isSelectedCaseRemoved || !props.caseId) {
          if (casesQuery.data.data.length > 0) {
            // if lastSelectedCase was deleted, we need to set it to null
            if (
              lastSelectedCaseIdRef.current &&
              !casesQuery.data.data.find(
                (x) => x.id === lastSelectedCaseIdRef.current,
              )
            ) {
              lastSelectedCaseIdRef.current = undefined;
            }

            const caseIdToSelect =
              lastSelectedCaseIdRef.current ??
              getLatestCase(casesQuery.data.data)?.id;

            openCaseTabOrEmpty(caseIdToSelect, true);
          } else {
            openCaseTabOrEmpty(undefined, true);
          }
        }
      },
      [props.caseId, casesQuery.data?.data?.length],
    );

    return (
      <>
        <Loading
          testId={'case-tab'}
          loading={
            casesQuery.isLoading ||
            isCaseBeingDeleted ||
            isCaseBeingAdded ||
            false
          }
          title={i18next.t('data_loading')}
          flex={true}
        >
          {(!casesQuery.data || casesQuery.data.data.length > 0) && (
            <div className={styles.caseBar}>
              <CaseBar
                patientId={patientId}
                currentCaseId={props.caseId}
                addCase={addCase}
                openCaseTab={openCaseTabOrEmpty}
              />
            </div>
          )}
          {(casesQuery.data?.data ?? []).map((caseItem) => {
            return (
              <Freeze freeze={caseItem.id !== caseId} key={caseItem.id}>
                <QueryMetaProvider meta={{ caseId: caseItem.id }}>
                  <CaseWrapper
                    patientId={patientId}
                    caseId={caseItem.id}
                    setCaseBeingDeleted={setCaseBeingDeleted}
                    openCaseTabOrEmpty={openCaseTabOrEmpty}
                  />
                </QueryMetaProvider>
              </Freeze>
            );
          })}

          {casesQuery.data?.data.length === 0 && (
            <div className={tableStyles.emptyList}>
              <div className={tableStyles.emptyListText}>
                {i18next.t('empty')}
              </div>
              <Button
                iconComponent={<AddCaseIcon />}
                color={ButtonColor.Default}
                width={ButtonWidth.Content}
                title={i18next.t('add_button')}
                onClick={addCase}
                testId="add_first_case_button"
              />
            </div>
          )}
        </Loading>
      </>
    );
  },
);
